import './style-app.css';

import { Spinner } from '@atoms';
import { useAppDispatch } from '@core/redux/hooks';
import { RootState } from '@core/redux/reducers';
import {
  selectIsAuth,
  selectIsRestoringAuth,
} from '@features/auth/store/auth.selectors';
import { tryRestoreAuth } from '@features/auth/store/auth.thunk';
import { getAllSegments } from '@features/segments/store/segments.thunk';
import { TenantThemeProvider } from '@service/tenant/TenantThemeContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, Spin, theme } from 'antd';
import ptBR from 'antd/lib/locale/pt_BR';
import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import styled from 'styled-components';

import { queryClient } from 'src/queryClient';

import { AppLayout } from '../features/layout/components/AppLayout';
import { selectCurrentLayout } from '../features/layout/selectors/layoutSelectors';
import { AppRoutes } from './Routes';

const AppRouterContent: FC = () => {
  const currentLayout = useSelector(selectCurrentLayout);

  if (currentLayout === 'application') {
    return (
      <AppLayout>
        <AppRoutes />
      </AppLayout>
    );
  }
  return <AppRoutes />;
};

const appRouter = createBrowserRouter(
  createRoutesFromElements(<Route path='*' element={<AppRouterContent />} />),
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

const App: FC = () => {
  const isRestoring = useSelector(selectIsRestoringAuth);
  const isAuth = useSelector(selectIsAuth);
  const { loadingApp } = useSelector((state: RootState) => state.layout);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(tryRestoreAuth());
  }, []);

  useEffect(() => {
    if (!isAuth) return;
    dispatch(getAllSegments());
  }, [isAuth]);

  const renderContent = function () {
    if (isRestoring === true) {
      return (
        <Centered>
          <Spin />
        </Centered>
      );
    }
    return <RouterProvider router={appRouter} />;
  };

  return (
    <>
      {loadingApp > 0 && <Spinner />}
      <div className='App'>
        <TenantThemeProvider>
          <ConfigProvider
            theme={{ algorithm: theme.darkAlgorithm }}
            locale={ptBR}
          >
            <QueryClientProvider client={queryClient}>
              {renderContent()}
            </QueryClientProvider>
          </ConfigProvider>
        </TenantThemeProvider>
      </div>
    </>
  );
};

ConfigProvider.config({
  holderRender: children => (
    <ConfigProvider
      prefixCls='ant'
      iconPrefixCls='anticon'
      theme={{ algorithm: theme.darkAlgorithm }}
    >
      {children}
    </ConfigProvider>
  ),
});

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export default App;
